import { useState, useRef, useEffect } from "react";
import { T } from "../tokens";

export function Stars({ rating = 0, size = 12, reviewCount }) {
  const full = Math.floor(rating), half = rating % 1 >= 0.5, empty = 5 - full - (half ? 1 : 0);
  const label = reviewCount != null
    ? `${rating} out of 5 stars, ${reviewCount} reviews`
    : `${rating} out of 5 stars`;
  return (
    <span
      role="img"
      aria-label={label}
      style={{ color:T.gold, fontSize:size, letterSpacing:1 }}
    >
      <span aria-hidden="true">{"★".repeat(full)}{half?"½":""  }{"☆".repeat(empty)}</span>
    </span>
  );
}

export function Toast({ msg, visible }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{ position:"fixed", top:28, left:"50%", zIndex:9999, transform:`translateX(-50%) translateY(${visible?0:-16}px)`, opacity:visible?1:0, transition:"all .3s", background:T.dark3, border:`1px solid ${T.goldBdrS}`, borderRadius:10, padding:"12px 24px", color:T.goldLt, fontSize:13.5, fontWeight:500, boxShadow:"0 8px 32px rgba(0,0,0,.6)", pointerEvents:"none", whiteSpace:"nowrap" }}
    >
      {msg}
    </div>
  );
}

export function useToast() {
  const [msg, setMsg] = useState(""); const [visible, setVis] = useState(false); const t = useRef(null);
  const show = (m) => { setMsg(m); setVis(true); clearTimeout(t.current); t.current = setTimeout(() => setVis(false), 2600); };
  return { msg, visible, show };
}

export function BtnGold({ children, onClick, disabled, full, size = "md", style = {}, type = "button", ariaLabel, ...rest }) {
  const pad = size==="sm"?"8px 18px":size==="lg"?"16px 36px":"12px 26px", fs = size==="sm"?12:size==="lg"?15:13.5;
  return (
    <button type={type} onClick={onClick} disabled={disabled} aria-label={ariaLabel} aria-disabled={disabled||undefined}
      onMouseOver={e=>{if(!disabled)e.currentTarget.style.transform="translateY(-2px)";}} onMouseOut={e=>{e.currentTarget.style.transform="";}}
      style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8, width:full?"100%":"auto", padding:pad, borderRadius:10, background:disabled?T.surface:`linear-gradient(135deg,${T.goldLt},${T.gold})`, border:`1px solid ${disabled?T.subtle:T.gold}`, color:disabled?T.subtle:T.dark, fontSize:fs, fontWeight:600, cursor:disabled?"not-allowed":"pointer", fontFamily:"inherit", letterSpacing:".05em", boxShadow:disabled?"none":`0 5px 22px rgba(201,168,76,.32)`, transition:"all .22s", ...style }}
      {...rest}>
      {children}
    </button>
  );
}

export function BtnGhost({ children, onClick, disabled, full, size = "md", style = {}, type = "button", ariaLabel, ...rest }) {
  const pad = size==="sm"?"7px 16px":size==="lg"?"15px 32px":"11px 22px", fs = size==="sm"?12:size==="lg"?15:13.5;
  return (
    <button type={type} onClick={onClick} disabled={disabled} aria-label={ariaLabel} aria-disabled={disabled||undefined}
      onMouseOver={e=>{if(!disabled){e.currentTarget.style.background=T.goldDim;e.currentTarget.style.borderColor=T.gold;}}} onMouseOut={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.borderColor=T.goldBdrS;}}
      style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8, width:full?"100%":"auto", padding:pad, borderRadius:10, background:"transparent", border:`1px solid ${disabled?T.subtle:T.goldBdrS}`, color:disabled?T.subtle:T.text, fontSize:fs, cursor:disabled?"not-allowed":"pointer", fontFamily:"inherit", letterSpacing:".04em", transition:"all .22s", ...style }}
      {...rest}>
      {children}
    </button>
  );
}

export function Spinner({ size = 36, label = "Loading" }) {
  return (
    <div
      role="status"
      aria-label={label}
      style={{ width:size, height:size, borderRadius:"50%", border:`2px solid ${T.goldBdr}`, borderTopColor:T.gold, animation:"fyzSpin .8s linear infinite" }}
    />
  );
}

export function SectionHeader({ eyebrow, title, action, onAction }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:32 }}>
      <div>
        {eyebrow && <div style={{ fontSize:10.5, color:T.gold, letterSpacing:".18em", textTransform:"uppercase", marginBottom:6 }}>{eyebrow}</div>}
        <div style={{ fontFamily:"Cormorant Garamond,serif", fontSize:"clamp(24px,3vw,34px)", fontWeight:400, color:T.text }}>{title}</div>
      </div>
      {action && <span onClick={onAction} style={{ fontSize:12.5, color:T.muted, cursor:"pointer", borderBottom:`1px solid ${T.goldBdr}`, paddingBottom:2, transition:"color .2s", whiteSpace:"nowrap" }} onMouseOver={e=>e.currentTarget.style.color=T.goldLt} onMouseOut={e=>e.currentTarget.style.color=T.muted}>{action}</span>}
    </div>
  );
}

export function Badge({ children, variant = "gold" }) {
  const s = { gold:{bg:T.goldDim,border:T.goldBdrS,color:T.goldLt}, green:{bg:"rgba(74,222,128,.1)",border:"rgba(74,222,128,.3)",color:T.green}, red:{bg:"rgba(248,113,113,.1)",border:"rgba(248,113,113,.3)",color:T.red}, subtle:{bg:T.dark4,border:T.goldBdr,color:T.muted} }[variant] || {};
  return <span style={{ display:"inline-block", padding:"2px 10px", borderRadius:10, background:s.bg, border:`1px solid ${s.border}`, color:s.color, fontSize:10.5, fontWeight:600, letterSpacing:".06em" }}>{children}</span>;
}

export function Divider() {
  return <div style={{ height:1, background:T.goldBdr, margin:"20px 0" }}/>;
}

export function Modal({ isOpen, onClose, title, children, footer, maxWidth = 500 }) {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center", padding:20, background:"rgba(0,0,0,.75)", backdropFilter:"blur(6px)", animation:"fyzFadeIn .2s ease-out" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background:T.dark2, border:`1px solid ${T.goldBdr}`, borderRadius:20, width:"100%", maxWidth, animation:"fyzFadeUp .3s cubic-bezier(0.34, 1.56, 0.64, 1)", boxShadow:"0 20px 50px rgba(0,0,0,.5)", overflow:"hidden" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 24px", borderBottom:`1px solid ${T.goldBdrS}15` }}>
          <h3 style={{ fontFamily:"Cormorant Garamond,serif", fontSize:24, color:T.text, fontWeight:500 }}>{title}</h3>
          <button onClick={onClose} style={{ background:"transparent", border:"none", color:T.muted, fontSize:20, cursor:"pointer", padding:4, display:"flex", alignItems:"center", justifyContent:"center" }} onMouseOver={e=>e.currentTarget.style.color=T.goldLt} onMouseOut={e=>e.currentTarget.style.color=T.muted}>✕</button>
        </div>
        {/* Body */}
        <div style={{ padding:"24px", fontSize:14.5, color:T.text, lineHeight:1.6 }}>
          {children}
        </div>
        {/* Footer */}
        {footer && (
          <div style={{ padding:"16px 24px", background:T.dark3, borderTop:`1px solid ${T.goldBdrS}15`, display:"flex", justifyContent:"flex-end", gap:12 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function ConfirmModal({ isOpen, onClose, onConfirm, title = "Are you sure?", message, confirmText = "Confirm", cancelText = "Cancel", variant = "gold", loading = false }) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth={420} footer={
      <>
        <BtnGhost onClick={onClose} disabled={loading}>{cancelText}</BtnGhost>
        <BtnGold onClick={onConfirm} disabled={loading} style={{ background: variant === "red" ? T.red : undefined, borderColor: variant === "red" ? T.red : undefined, color: variant === "red" ? "#fff" : undefined }}>
          {loading ? <Spinner size={16} /> : confirmText}
        </BtnGold>
      </>
    }>
      <div style={{ color:T.muted }}>{message}</div>
    </Modal>
  );
}
